import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authenticatedFetch, getUser } from '@/lib/auth';
import {
  CheckCircle2, FileText, Shield, Award, Loader2,
  BookOpen, Users, ClipboardCheck, Activity, Lock, Archive
} from 'lucide-react';

interface OnboardingStatus {
  required: boolean;
  reason?: string;
  organization_signed: boolean;
  organization_signed_version: string | null;
  organization_signed_at: string | null;
  organization_signed_by: string | null;
  current_template_version: string | null;
  org_signed_this_version: boolean;
  user_ack_this_version: boolean;
  user: {
    id: string;
    full_name: string;
    email: string;
    member_id: string | null;
    member_name: string | null;
  };
}

interface Template {
  id: string;
  template_name: string;
  content: string;
  version: string;
}

interface QualityPoint {
  n: number;
  heading: string;
  text: string;
  confirm: string;
}
interface QualityCategory {
  id: string;
  title: string;
  icon: typeof BookOpen;
  color: string;
  points: QualityPoint[];
}

// 16 strukturerte punkter — speiler `legal_templates`-malen, men i UI-vennlig form.
// Punkt-numrene MÅ matche templateens nummerering for revisjons-spor.
const QUALITY_CATEGORIES: QualityCategory[] = [
  {
    id: 'A',
    title: 'Studieplaner og innhold',
    icon: BookOpen,
    color: 'kf-primary',
    points: [
      {
        n: 1,
        heading: 'Godkjente studieplaner',
        text: 'Alle kurs som arrangeres følger en studieplan som er godkjent i kvalitetssikringssystemet. Avvik fra godkjent studieplan rapporteres til studieforbundet før kursstart.',
        confirm: 'Vi sørger for at alle kurs vi arrangerer følger godkjent studieplan, og varsler studieforbundet om avvik før kursstart.'
      },
      {
        n: 2,
        heading: 'Innholdssamsvar',
        text: 'Innholdet i kurset er i samsvar med studieplanens læringsutbytter, omfang (timer/ECTS) og målgruppe.',
        confirm: 'Vi vil sikre at kursinnholdet samsvarer med studieplanens læringsutbytter, omfang og målgruppe.'
      },
      {
        n: 3,
        heading: 'Kontinuerlig forbedring',
        text: 'Studieplaner gjennomgås minst årlig og oppdateres ved behov for å reflektere endringer i fag, lovverk eller målgruppe.',
        confirm: 'Vi gjennomgår våre studieplaner minst årlig og oppdaterer ved behov.'
      }
    ]
  },
  {
    id: 'B',
    title: 'Fagansvar og kompetanse',
    icon: Users,
    color: 'kf-primary',
    points: [
      {
        n: 4,
        heading: 'Fagansvarlig per studieplan',
        text: 'Hver godkjent studieplan har en utpekt fagansvarlig med dokumentert kompetanse innen fagfeltet.',
        confirm: 'Vi har utpekt fagansvarlig med dokumentert kompetanse for hver studieplan.'
      },
      {
        n: 5,
        heading: 'Kvalifiserte kursledere/instruktører',
        text: 'Kursledere og instruktører er kvalifisert for emnet, enten ved relevant utdanning eller dokumentert yrkeserfaring.',
        confirm: 'Våre kursledere og instruktører er kvalifisert ved utdanning eller dokumentert erfaring.'
      },
      {
        n: 6,
        heading: 'Oppdatert oversikt',
        text: 'Lister over fagansvarlige og kursledere holdes oppdatert i organisasjonen og kan fremlegges på forespørsel.',
        confirm: 'Vi holder lister over fagansvarlige og kursledere oppdatert og kan fremlegge dem ved forespørsel.'
      }
    ]
  },
  {
    id: 'C',
    title: 'Vurdering og validering',
    icon: ClipboardCheck,
    color: 'kf-primary',
    points: [
      {
        n: 7,
        heading: 'Vurderingsformer',
        text: 'Vurderingsformene som er beskrevet i studieplanen følges (eksamen, arbeidskrav, observasjon, sensor, etc.).',
        confirm: 'Vi følger de vurderingsformene som er beskrevet i hver studieplan.'
      },
      {
        n: 8,
        heading: 'Sensorers uavhengighet',
        text: 'Sensorer er upartiske, kvalifiserte og har ingen interessekonflikt med kursdeltaker eller kursleder.',
        confirm: 'Vi sikrer at sensorer er upartiske, kvalifiserte og uten interessekonflikt.'
      },
      {
        n: 9,
        heading: 'Klagerett',
        text: 'Klagerett og klageprosess kommuniseres til kursdeltakere før kursstart, og klager behandles i tråd med organisasjonens rutiner.',
        confirm: 'Vi informerer deltakere om klagerett før kursstart og behandler klager etter våre rutiner.'
      }
    ]
  },
  {
    id: 'D',
    title: 'Internkontroll og stikkprøver',
    icon: Activity,
    color: 'kf-primary',
    points: [
      {
        n: 10,
        heading: 'Stikkprøver',
        text: 'Stikkprøver av kursgjennomføring utføres minst halvårlig. Prøvene dokumenteres og rapporteres internt.',
        confirm: 'Vi gjennomfører dokumenterte stikkprøver av kursgjennomføring minst halvårlig.'
      },
      {
        n: 11,
        heading: 'Avvikshåndtering',
        text: 'Avvik dokumenteres og lukkes med tiltak. Alvorlige avvik rapporteres til studieforbundet umiddelbart.',
        confirm: 'Vi dokumenterer avvik og lukker dem med tiltak. Alvorlige avvik varsles studieforbundet umiddelbart.'
      },
      {
        n: 12,
        heading: 'Årlig rapport',
        text: 'En årlig kvalitetsrapport oversendes organisasjonens styre/ledelse med oversikt over avvik, tiltak og forbedringer.',
        confirm: 'Vi utarbeider og oversender årlig kvalitetsrapport til styret/ledelsen.'
      }
    ]
  },
  {
    id: 'E',
    title: 'GDPR og personvern',
    icon: Lock,
    color: 'kf-primary',
    points: [
      {
        n: 13,
        heading: 'Lovlig behandling',
        text: 'Personopplysninger om kursdeltakere behandles i samsvar med personvernforordningen (GDPR) og personopplysningsloven.',
        confirm: 'Vi behandler personopplysninger i samsvar med GDPR og personopplysningsloven.'
      },
      {
        n: 14,
        heading: 'Åpenhet',
        text: 'Deltakere informeres om hvilke data som registreres, hvilket grunnlag behandlingen har, og hvor lenge data oppbevares.',
        confirm: 'Vi informerer deltakere om hvilke data vi registrerer, behandlingsgrunnlag og oppbevaringstid.'
      }
    ]
  },
  {
    id: 'F',
    title: 'Etterprøvbarhet',
    icon: Archive,
    color: 'kf-primary',
    points: [
      {
        n: 15,
        heading: 'Dokumentasjonsoppbevaring',
        text: 'Dokumentasjon av kursgjennomføring (deltakerlister, vurderinger, attest-utstedelser) oppbevares i minst 5 år, i tråd med voksenopplæringsloven og forvaltningsloven.',
        confirm: 'Vi oppbevarer kursdokumentasjon i minst 5 år i tråd med lovverket.'
      },
      {
        n: 16,
        heading: 'Innsyn på forespørsel',
        text: 'Forespørsler om dokumentasjon fra Kompetanseforbundet, studieforbund eller offentlige myndigheter besvares innen 14 dager.',
        confirm: 'Vi besvarer dokumentasjonsforespørsler innen 14 dager.'
      }
    ]
  }
] as const;

const POINT_COUNT = 16;
const FLAT_POINTS = QUALITY_CATEGORIES.flatMap(c => c.points);

export default function QualityAssuranceOnboarding() {
  const navigate = useNavigate();
  const user = getUser();

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [template, setTemplate] = useState<Template | null>(null);
  const [acceptedPoints, setAcceptedPoints] = useState<Set<number>>(new Set());
  const [signatureText, setSignatureText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ contractId: string } | null>(null);
  const [showFullContract, setShowFullContract] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [statusRes, tmplRes] = await Promise.all([
          authenticatedFetch('/api/onboarding/status').then(r => r.json()),
          authenticatedFetch('/api/onboarding/template').then(r => r.json())
        ]);
        if (!statusRes.success) {
          setError(statusRes.error || 'Kunne ikke hente status');
          return;
        }
        setStatus(statusRes);
        if (tmplRes.success) setTemplate(tmplRes.template);
        if (user?.full_name) setSignatureText(user.full_name);
      } catch (e: any) {
        setError(e.message || 'Feil ved lasting');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const togglePoint = (n: number) => {
    setAcceptedPoints(prev => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n); else next.add(n);
      return next;
    });
  };

  const acceptCategory = (categoryId: string) => {
    const cat = QUALITY_CATEGORIES.find(c => c.id === categoryId);
    if (!cat) return;
    setAcceptedPoints(prev => {
      const next = new Set(prev);
      cat.points.forEach(p => next.add(p.n));
      return next;
    });
  };

  const allAccepted = acceptedPoints.size === POINT_COUNT;
  const isFirstSigner = status && !status.organization_signed;
  const isAcknowledgeOnly = status && status.organization_signed && status.org_signed_this_version && !status.user_ack_this_version;

  const handleOrgSign = async () => {
    if (!allAccepted || !signatureText.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await authenticatedFetch('/api/onboarding/organization-sign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signature_text: signatureText.trim(),
          accepted_points: Array.from(acceptedPoints).sort((a, b) => a - b)
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Kunne ikke signere');
        return;
      }
      setSuccess({ contractId: data.contract.id });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUserAck = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await authenticatedFetch('/api/onboarding/user-acknowledge', { method: 'POST' });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Kunne ikke bekrefte');
        return;
      }
      navigate('/');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-kf-primary" />
      </div>
    );
  }

  if (status && !status.required) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h1 className="text-2xl font-bold text-green-800">Du er klar til å bruke systemet</h1>
          <p className="mt-2 text-green-700">Onboarding er ikke nødvendig for din rolle.</p>
          <button onClick={() => navigate('/')} className="mt-4 px-4 py-2 bg-kf-primary text-white rounded-md hover:bg-kf-dark">
            Gå til dashboard
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <div className="bg-white border border-green-200 rounded-lg shadow-sm p-8 text-center">
          <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Kontrakt signert</h1>
          <p className="text-gray-700 mb-6">
            Kvalitetssikrings-kontrakten er signert på vegne av {status?.user.member_name}.
            Kvitterings-e-post er sendt til deg, organisasjonens kontaktadresser, studieforbund-admin og systemadmin.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              type="button"
              onClick={async () => {
                try {
                  const res = await fetch(`/api/onboarding/contract-pdf/${success.contractId}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` }
                  });
                  if (!res.ok) {
                    alert('Kunne ikke hente PDF: ' + res.status);
                    return;
                  }
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  window.open(url, '_blank');
                  setTimeout(() => URL.revokeObjectURL(url), 60_000);
                } catch (e: any) {
                  alert('Feil ved nedlasting: ' + (e.message || e));
                }
              }}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Last ned PDF
            </button>
            <button onClick={() => navigate('/')} className="px-4 py-2 bg-kf-primary text-white rounded-md hover:bg-kf-dark">
              Fortsett til systemet
            </button>
          </div>
        </div>
      </div>
    );
  }

  const heading = isFirstSigner
    ? `${status?.user.member_name} må bekrefte kvalitetskriteriene før systemet kan tas i bruk`
    : 'Bekreft at du forplikter deg til organisasjonens kvalitetsrutiner';

  const intro = isFirstSigner
    ? 'Som første bruker fra organisasjonen signerer du kontrakten på vegne av hele organisasjonen. Senere brukere bekrefter individuelt at de forplikter seg til å følge de samme retningslinjene.'
    : `${status?.user.member_name} har allerede signert kvalitetssikrings-kontrakten. Som ny bruker må du personlig bekrefte at du har lest og forstått punktene, og at du forplikter deg til å følge dem.`;

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
      {/* Hero */}
      <div className="bg-kf-primary text-white rounded-t-lg px-6 py-6 border-t-4 border-kf-accent">
        <div className="flex items-start gap-4">
          <Shield className="w-10 h-10 text-kf-accent flex-shrink-0 mt-1" />
          <div>
            <h1 className="text-2xl font-bold">Kvalitetssikring av kursvirksomheten</h1>
            <p className="text-sm text-white/90 mt-1">{heading}</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-t-0 border-gray-200 rounded-b-lg shadow-sm">
        {/* Intro */}
        <div className="px-6 py-5 border-b border-gray-200">
          <p className="text-sm text-gray-700 leading-relaxed">{intro}</p>
          {status?.organization_signed && status.organization_signed_at && (
            <div className="mt-3 inline-flex items-center gap-2 text-xs bg-blue-50 text-blue-900 border border-blue-200 rounded-full px-3 py-1">
              <Award className="w-3.5 h-3.5" />
              Organisasjonen signerte versjon {status.organization_signed_version} den{' '}
              {new Date(status.organization_signed_at).toLocaleDateString('nb-NO', { dateStyle: 'long' })}
              {status.organization_signed_by ? ` av ${status.organization_signed_by}` : ''}.
            </div>
          )}
          {template && (
            <p className="mt-3 text-xs text-gray-500">
              Mal-versjon: {template.version} ·{' '}
              <button
                type="button"
                onClick={() => setShowFullContract(!showFullContract)}
                className="text-kf-primary hover:underline"
              >
                {showFullContract ? 'Skjul' : 'Vis'} full kontraktstekst
              </button>
            </p>
          )}
          {showFullContract && template && (
            <pre className="mt-3 max-h-96 overflow-y-auto text-xs bg-gray-50 border border-gray-200 rounded-md p-3 whitespace-pre-wrap text-gray-700">
              {template.content}
            </pre>
          )}
        </div>

        {/* Fremdrift */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-3 text-sm">
          <div className="flex-1">
            <div className="flex items-center justify-between text-gray-700 mb-1">
              <span>Bekreftet</span>
              <span className="font-medium">{acceptedPoints.size} av {POINT_COUNT}</span>
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-kf-primary transition-all"
                style={{ width: `${(acceptedPoints.size / POINT_COUNT) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Kategorier med punkter */}
        <div className="px-4 sm:px-6 py-5 space-y-6">
          {QUALITY_CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const acceptedInCat = cat.points.filter(p => acceptedPoints.has(p.n)).length;
            const allInCatAccepted = acceptedInCat === cat.points.length;
            return (
              <section key={cat.id} className="border border-gray-200 rounded-lg overflow-hidden">
                <header className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-md bg-kf-primary text-white flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-gray-900">
                        {cat.id}. {cat.title}
                      </h2>
                      <p className="text-xs text-gray-500">{acceptedInCat} av {cat.points.length} bekreftet</p>
                    </div>
                  </div>
                  {!allInCatAccepted && (
                    <button
                      type="button"
                      onClick={() => acceptCategory(cat.id)}
                      className="text-xs text-kf-primary hover:underline whitespace-nowrap"
                    >
                      Hak av alle i kategorien
                    </button>
                  )}
                </header>
                <ul className="divide-y divide-gray-100">
                  {cat.points.map(p => {
                    const checked = acceptedPoints.has(p.n);
                    return (
                      <li
                        key={p.n}
                        className={`px-4 py-4 transition-colors ${checked ? 'bg-green-50/40' : 'bg-white hover:bg-gray-50'}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${
                              checked
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-200 text-gray-700'
                            }`}>
                              {checked ? <CheckCircle2 className="w-4 h-4" /> : p.n}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-gray-900">{p.heading}</h3>
                            <p className="mt-1 text-sm text-gray-700 leading-relaxed">{p.text}</p>
                            <label className={`mt-3 flex items-start gap-2 p-3 rounded-md border cursor-pointer transition-colors ${
                              checked
                                ? 'border-green-300 bg-green-50'
                                : 'border-gray-200 bg-white hover:border-kf-primary/40 hover:bg-kf-primary/5'
                            }`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => togglePoint(p.n)}
                                className="mt-0.5 h-4 w-4 text-kf-primary rounded focus:ring-2 focus:ring-kf-primary"
                              />
                              <span className={`text-sm ${checked ? 'text-green-900' : 'text-gray-800'}`}>
                                <span className="font-medium">Jeg bekrefter:</span> {p.confirm}
                              </span>
                            </label>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>

        {/* Signatur og innsending */}
        <div className="px-6 py-5 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          {isFirstSigner ? (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Signatur (fullt navn) *
                </label>
                <input
                  type="text"
                  value={signatureText}
                  onChange={e => setSignatureText(e.target.value)}
                  placeholder="f.eks. Kari Nordmann"
                  className="w-full sm:max-w-md px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-kf-primary"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Du signerer på vegne av {status?.user.member_name}. Tidspunkt og IP-adresse loggføres som revisjonsspor.
                </p>
              </div>

              {error && (
                <div className="mb-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
                  {error}
                </div>
              )}

              <button
                onClick={handleOrgSign}
                disabled={!allAccepted || !signatureText.trim() || submitting}
                className="w-full sm:w-auto px-6 py-3 bg-kf-primary text-white rounded-md hover:bg-kf-dark disabled:opacity-50 font-medium flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signerer…
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4" />
                    Signer kontrakten ({acceptedPoints.size}/{POINT_COUNT})
                  </>
                )}
              </button>
              {!allAccepted && (
                <p className="mt-2 text-xs text-gray-500">
                  Hak av alle 16 punkter ovenfor for å aktivere signering.
                </p>
              )}
            </>
          ) : isAcknowledgeOnly ? (
            <>
              {error && (
                <div className="mb-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
                  {error}
                </div>
              )}
              <button
                onClick={handleUserAck}
                disabled={!allAccepted || submitting}
                className="w-full sm:w-auto px-6 py-3 bg-kf-primary text-white rounded-md hover:bg-kf-dark disabled:opacity-50 font-medium flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Bekrefter…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Bekreft personlig forpliktelse ({acceptedPoints.size}/{POINT_COUNT})
                  </>
                )}
              </button>
              {!allAccepted && (
                <p className="mt-2 text-xs text-gray-500">
                  Hak av alle 16 punkter ovenfor for å aktivere bekreftelse.
                </p>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// Bruker FLAT_POINTS for fremtidige operasjoner (filtrering, summary, etc.)
void FLAT_POINTS;
